// Panel de sincronización individual con Discogs (Tarea 19A, Req. 10.1, 10.6–10.9).
//
// "Actualizar desde Discogs" → lista de candidatas con puntaje y DESGLOSE de
// variables; el usuario elige una candidata; se muestra la COMPARACIÓN campo a
// campo con acciones aceptar todo / selectivo / rechazar. Al aplicar, se envían
// solo los campos aprobados.

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useApi } from '../api/ApiContext.js';
import { AlertMessage, ButtonSpinner, Card, Icon } from '../ui/components.js';
import { ScoreBreakdown } from './ScoreBreakdown.js';
import { fieldLabel } from './syncFieldLabels.js';
import type { RecordDiff, ScoredCandidate } from '../types.js';

export function RecordSyncPanel({ recordId, onApplied }: { recordId: string; onApplied: () => void }) {
  const { t } = useTranslation();
  const api = useApi();
  const [candidates, setCandidates] = useState<ScoredCandidate[] | null>(null);
  const [diff, setDiff] = useState<RecordDiff | null>(null);
  const [chosen, setChosen] = useState<number | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  // Acción en curso (para el spinner en el botón correcto). `null` = inactivo.
  // `choose:{id}` distingue la candidata que se está cargando.
  const [action, setAction] = useState<
    null | 'search' | 'reject' | 'accept_selective' | `choose:${number}`
  >(null);
  const busy = action !== null;

  async function loadCandidates() {
    setAction('search');
    setError(null);
    setMessage(null);
    try {
      const res = await api.records.sync(recordId);
      setCandidates(res.candidates);
      setDiff(res.diff);
      setChosen(res.diff?.discogsReleaseId ?? res.candidates[0]?.discogsReleaseId ?? null);
      setSelected(new Set((res.diff?.changedFields ?? []).map((f) => f.field)));
    } catch (err) {
      setError(err instanceof Error ? err.message : t('recordSync.errQuery'));
    } finally {
      setAction(null);
    }
  }

  async function chooseCandidate(releaseId: number) {
    setAction(`choose:${releaseId}`);
    setError(null);
    try {
      const res = await api.records.sync(recordId, releaseId);
      setDiff(res.diff);
      setChosen(releaseId);
      setSelected(new Set((res.diff?.changedFields ?? []).map((f) => f.field)));
    } catch (err) {
      setError(err instanceof Error ? err.message : t('recordSync.errLoadComparison'));
    } finally {
      setAction(null);
    }
  }

  async function apply(mode: 'accept_all' | 'accept_selective' | 'reject') {
    if (chosen == null) return;
    setAction(mode === 'reject' ? 'reject' : 'accept_selective');
    setError(null);
    try {
      const fields = mode === 'accept_selective' ? [...selected] : undefined;
      await api.records.syncApply(recordId, chosen, mode, fields);
      setMessage(mode === 'reject' ? t('recordSync.noChangesMarked') : t('recordSync.changesApplied'));
      setCandidates(null);
      setDiff(null);
      onApplied();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('recordSync.errApply'));
    } finally {
      setAction(null);
    }
  }

  function toggle(field: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(field)) next.delete(field);
      else next.add(field);
      return next;
    });
  }

  return (
    <Card title={t('recordSync.title')} icon="sync" collapsible defaultOpen={false}>
      <AlertMessage message={error} className="mb-space-sm" />
      {message && (
        <div className="p-space-sm rounded-lg bg-goldmine-mint/15 border border-goldmine-mint/40 text-goldmine-mint font-body-sm text-body-sm mb-space-sm">
          {message}
        </div>
      )}

      {!candidates && (
        <button className="btn-primary w-full" type="button" onClick={() => void loadCandidates()} disabled={busy}>
          {action === 'search' ? <ButtonSpinner /> : <Icon name="cloud_sync" className="text-[18px]" />}
          {action === 'search' ? t('recordSync.querying') : t('recordSync.update')}
        </button>
      )}

      {candidates && (
        <div className="flex flex-col gap-space-sm">
          <span className="section-label text-vacuum-glow">{t('recordSync.candidatesDetected', { count: candidates.length })}</span>
          <ul className="flex flex-col gap-space-sm">
            {candidates.map((c) => {
              const isChosen = chosen === c.discogsReleaseId;
              return (
                <li
                  key={c.discogsReleaseId ?? c.title}
                  className={`surface-rack-card !p-space-sm flex flex-col gap-space-xs ${isChosen ? 'border-primary-container' : ''}`}
                >
                  <div className="flex items-start justify-between gap-space-sm">
                    <div className="min-w-0">
                      <div className="font-body-md text-body-md text-on-surface truncate">{c.title}</div>
                      <div className="font-body-sm text-body-sm text-on-surface-variant truncate">
                        {c.year ?? '—'} · {c.label ?? ''} · {c.catalogNumber ?? ''} · {c.country ?? ''}
                      </div>
                    </div>
                    <button
                      className={isChosen ? 'btn-primary btn-sm' : 'btn-secondary btn-sm'}
                      type="button"
                      disabled={busy || c.discogsReleaseId == null}
                      onClick={() => c.discogsReleaseId != null && void chooseCandidate(c.discogsReleaseId)}
                    >
                      {action === `choose:${c.discogsReleaseId}` && <ButtonSpinner className="text-[14px]" />}
                      {isChosen ? t('recordSync.chosen') : t('recordSync.choose')}
                    </button>
                  </div>
                  {/* Barra de confianza (gradiente ámbar→cian) */}
                  <div className="flex items-center gap-space-xs">
                    <div className="meter-track flex-1">
                      <div
                        className="h-full bg-gradient-to-r from-goldmine-vg to-primary-container"
                        style={{ width: `${c.score}%` }}
                      />
                    </div>
                    <span className="font-code-catalog text-body-sm text-primary font-bold w-10 text-right">
                      {c.score.toFixed(0)}%
                    </span>
                    <ScoreBreakdown breakdown={c.breakdown} />
                  </div>
                </li>
              );
            })}
          </ul>

          {diff && (
            <>
              <span className="section-label text-vacuum-glow mt-space-sm">{t('recordSync.fieldComparison')}</span>
              {diff.fields.length === 0 || !diff.hasChanges ? (
                <p className="font-body-sm text-body-sm text-on-surface-variant">
                  {t('recordSync.noDifferences')}
                </p>
              ) : (
                <ul className="flex flex-col gap-space-xs">
                  {diff.changedFields.map((f) => (
                    <li key={f.field} className="surface-rack-card !p-space-sm flex items-start gap-space-sm">
                      <input
                        type="checkbox"
                        className="mt-1 accent-[#38bdf8] w-4 h-4"
                        checked={selected.has(f.field)}
                        onChange={() => toggle(f.field)}
                      />
                      <div className="flex-1 min-w-0">
                        <span className="font-headline-sm text-body-sm text-goldmine-vg">{fieldLabel(t, f.field)}</span>
                        <div className="flex items-center gap-space-xs font-code-catalog text-body-sm mt-0.5">
                          <span className="text-on-surface-variant line-through truncate">{renderValue(f.current)}</span>
                          <Icon name="arrow_forward" className="text-[14px] text-on-surface-variant shrink-0" />
                          <span className="text-goldmine-mint truncate">{renderValue(f.proposed)}</span>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}

              {/* Dos acciones: "Mantener" (no aplica cambios, marca al día) a la
                  izquierda; "Cambiar" (aplica los campos seleccionados) a la
                  derecha. */}
              <div className="grid grid-cols-2 gap-space-sm mt-space-sm">
                <button
                  className="btn-ghost !h-11 !px-space-sm"
                  type="button"
                  disabled={busy}
                  onClick={() => void apply('reject')}
                >
                  {action === 'reject' ? <ButtonSpinner className="text-[15px]" /> : null}
                  {action === 'reject' ? t('recordSync.saving') : t('recordSync.keep')}
                </button>
                <button
                  className="btn-primary !h-11 !px-space-sm"
                  type="button"
                  disabled={busy || !diff.hasChanges || selected.size === 0}
                  onClick={() => void apply('accept_selective')}
                >
                  {action === 'accept_selective' ? (
                    <ButtonSpinner className="text-[18px]" />
                  ) : (
                    <Icon name="cloud_done" className="text-[18px]" />
                  )}
                  {action === 'accept_selective' ? t('recordSync.applying') : t('recordSync.change')}
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </Card>
  );
}

function renderValue(v: unknown): string {
  if (v === null || v === undefined) return '—';
  if (Array.isArray(v)) return v.join(', ');
  return String(v);
}
